import {
	DESIGN_PRESET_PROPERTIES,
	getDesignPresetInheritedAssignment,
	getDesignPresetTokenId,
	type DesignPreset,
} from 'common/design-tokens';
import { getPlatformReferenceWidth } from 'common/utils/story-media-query';
import type { StoryDefaultPlatform, StoryMediaPlatform } from 'types/story';
import {
	getDesignPresetDisplayValue,
	getDesignPresetPlatformName,
	getDesignPresetPreviewStyle,
	orderDesignPresetPlatforms,
} from './design-preset-display';

type SummaryContext = Omit<Parameters<typeof getDesignPresetDisplayValue>[0], 'property' | 'assignment' | 'platform'>;

/** Summaries describe the resource, without incorporating a block's local overrides. */
export function getDesignPresetSummary({
	preset,
	platform,
	...context
}: SummaryContext & { preset: DesignPreset; platform?: StoryMediaPlatform }) {
	const requestedPlatform = !platform || platform === 'default' ? context.mediaQuery.defaultPlatform : platform;
	const currentPlatform = requestedPlatform === 'default' ? 'desktop' : requestedPlatform;
	const platforms = orderDesignPresetPlatforms(context.mediaQuery, [preset]).filter(item => item.key !== 'default');
	const currentPlatformLabel = context.mediaQuery.config[currentPlatform]?.uiName;
	const properties = DESIGN_PRESET_PROPERTIES.flatMap(property => {
		const assignment = preset.properties[property];
		if (!assignment) return [];
		const values = platforms.map(({ key, config }) => {
			const target = key as StoryDefaultPlatform;
			const inherited = getDesignPresetInheritedAssignment(assignment, target, context.mediaQuery);
			const display = getDesignPresetDisplayValue({ ...context, property, assignment, platform: target });
			const assigned =
				inherited?.source === target ||
				(inherited?.source === 'Base' && target === context.mediaQuery.defaultPlatform);
			let inheritance: 'empty' | 'assigned' | 'inherited' = 'empty';
			if (inherited) inheritance = assigned ? 'assigned' : 'inherited';
			return {
				platform: target,
				label: getDesignPresetPlatformName(target, config.uiName),
				source:
					inherited &&
					getDesignPresetPlatformName(inherited.source, context.mediaQuery.config[inherited.source]?.uiName),
				inheritance,
				value: display?.value,
				unresolved: !!inherited && !!getDesignPresetTokenId(inherited.value) && !display,
			};
		});
		return [{ property, values, current: values.find(item => item.platform === currentPlatform) }];
	});
	const currentValue = (property: (typeof DESIGN_PRESET_PROPERTIES)[number]) =>
		properties.find(item => item.property === property)?.current?.value;
	const fontLabel = [currentValue('fontFamily'), currentValue('fontWeight')]
		.filter(value => value !== undefined)
		.join(' · ');
	const fontSize = properties.find(item => item.property === 'fontSize');
	// Inspector describes the active platform; library rows list only authored platforms.
	const currentSizes = fontSize?.current ? [fontSize.current] : [];
	const assignedSizes = fontSize?.values.filter(item => item.inheritance === 'assigned');
	const sizes = platform ? currentSizes : assignedSizes;
	const typographyProperties = ['fontFamily', 'fontWeight', 'fontSize', 'lineHeight', 'letterSpacing'];
	const hasTypographySummary = !!fontLabel || !!sizes?.length;
	const details = properties.filter(item => !hasTypographySummary || !typographyProperties.includes(item.property));
	const preview = getDesignPresetPreviewStyle({ ...context, preset, platform: currentPlatform });
	return {
		currentPlatform,
		currentPlatformLabel: getDesignPresetPlatformName(currentPlatform, currentPlatformLabel),
		currentPlatformWidth: getPlatformReferenceWidth(context.mediaQuery, currentPlatform),
		properties,
		fontLabel,
		sizes,
		details,
		isTypography: properties.some(item => typographyProperties.includes(item.property)),
		unresolved: properties.some(item => item.values.some(value => value.unresolved)),
		// Aa conveys weight; the full Preview loads and verifies the actual font family.
		sampleStyle: { fontWeight: preview.fontWeight },
		backgroundColor: currentValue('backgroundColor'),
		borderColor: currentValue('borderColor'),
	};
}
